import React from 'react';
import { BASENAME } from '../../services/api';
import { UI_THEME } from '../../ui/theme';

type OfficialFieldProps = {
  label: string;
  value?: React.ReactNode;
  className?: string;
};

export const OfficialField: React.FC<OfficialFieldProps> = ({ label, value, className = '' }) => (
  <div className={`rounded-md border border-slate-200 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-900/70 print:border-slate-200 print:bg-white ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 print:text-slate-500">{label}</div>
    <div className="mt-0.5 text-[13px] leading-5 text-slate-900 dark:text-slate-100 print:text-slate-900">{value ?? 'Não informado'}</div>
  </div>
);

type OfficialSectionTitleProps = {
  number: string;
  title: string;
  aside?: React.ReactNode;
};

export const OfficialSectionTitle: React.FC<OfficialSectionTitleProps> = ({ number, title, aside }) => (
  <div className="mb-3 mt-8 flex flex-col gap-2 border-b border-slate-300 pb-2 dark:border-slate-700 print:border-slate-300 sm:flex-row sm:items-end sm:justify-between">
    <h2 className="text-base font-bold uppercase tracking-[0.12em] text-slate-900 dark:text-slate-100 print:text-slate-900">
      {number} - {title}
    </h2>
    {aside ? <div className="text-xs text-slate-500 dark:text-slate-400 print:text-slate-500">{aside}</div> : null}
  </div>
);

type OfficialTableCellProps = React.ThHTMLAttributes<HTMLTableCellElement> &
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    head?: boolean;
    align?: 'left' | 'center' | 'right';
  };

export const OfficialTableCell: React.FC<OfficialTableCellProps> = ({
  children,
  head,
  align = 'left',
  className = '',
  ...props
}) => {
  const Tag = head ? 'th' : 'td';
  const alignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  return (
    <Tag
      className={`whitespace-normal break-words border border-slate-300 px-2 py-2 align-top text-xs dark:border-slate-700 print:border-slate-300 ${alignment} ${
        head
          ? 'bg-slate-100 font-semibold uppercase tracking-[0.08em] text-slate-700 dark:bg-slate-800 dark:text-slate-200 print:bg-slate-100 print:text-slate-700'
          : 'text-slate-800 dark:text-slate-200 print:text-slate-800'
      } ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
};

type OfficialReportLayoutProps = {
  moduleLabel: string;
  title: string;
  subtitle: string;
  toolbar?: React.ReactNode;
  summaryFields?: React.ReactNode;
  children: React.ReactNode;
};

const OfficialReportLayout: React.FC<OfficialReportLayoutProps> = ({
  moduleLabel,
  title,
  subtitle,
  toolbar,
  summaryFields,
  children,
}) => {
  const isSaas = UI_THEME === 'saas';
  const accentLineClass = isSaas ? 'border-teal-200 dark:border-teal-900/70' : 'border-blue-200 dark:border-blue-900/70';

  return (
    <div className="min-h-full bg-slate-100 dark:bg-slate-950 print:bg-white">
      {toolbar ? (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          {toolbar}
        </div>
      ) : null}

      <main className="w-full rounded-3xl bg-white px-6 py-8 shadow-xl dark:bg-slate-900 print:rounded-none print:bg-white print:shadow-none">
        <header className={`border-b pb-5 ${accentLineClass} print:border-slate-300`}>
          <div className="mb-4 flex items-center justify-center gap-4">
            <img
              src={`${BASENAME || ''}/favicon.ico`}
              alt="Brasão de Sinop"
              className="h-14 w-14 object-contain print:h-12 print:w-12"
            />
            <div className="text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400 print:text-slate-500">
                Prefeitura Municipal de Sinop - MT
              </p>
              <p className="mt-1 text-sm font-semibold tracking-[0.08em] text-slate-700 dark:text-slate-200 print:text-slate-700">
                {isSaas ? 'Gestão de Emendas Municipais' : 'Sistema de Gestão de Emendas Municipais'}
              </p>
            </div>
          </div>
          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400 print:text-slate-500">{moduleLabel}</p>
          <h1 className="mt-2 text-center text-2xl font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-slate-100 print:text-slate-900">{title}</h1>
          <p className="mx-auto mt-3 max-w-3xl text-center text-sm leading-6 text-slate-600 dark:text-slate-300 print:text-slate-600">{subtitle}</p>
          {summaryFields ? (
            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
              {summaryFields}
            </div>
          ) : null}
        </header>

        {children}
      </main>
    </div>
  );
};

export default OfficialReportLayout;
